import API_BASE from '../config';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from "react-hot-toast";
import { creditAwareFetch } from '../hooks/creditAwareFetch';
import { useAuth } from '@clerk/clerk-react';
import SequenceConfigPanel from './SequenceConfigPanel';

const CONTACT_STATUSES = ['new','contacted','replied','meeting booked','closed'];
const STATUS_COLOURS = { new: '#888', contacted: '#2B6CB0', replied: '#B7791F', 'meeting booked': '#276749', closed: '#4A5568' };
const STATUS_BG = { new: '#F7F7F7', contacted: '#EBF8FF', replied: '#FFFBEB', 'meeting booked': '#F0FFF4', closed: '#F7FAFC' };
const SIGNAL_LABELS = { funding: 'Funding Round', hiring: 'Hiring Activity', leadership_change: 'Leadership Change', news: 'Company News', tech_change: 'Technology Change' };
const SIGNAL_COLOURS = { funding: '#276749', hiring: '#2B6CB0', leadership_change: '#8B1A1A', news: '#B7791F', tech_change: '#553C9A' };
const SIGNAL_BG = { funding: '#F0FFF4', hiring: '#EBF8FF', leadership_change: '#FFF5F5', news: '#FFFFF0', tech_change: '#FAF5FF' };

function ContactPanel({ contactId, clientId, onClose, onSequence, onDelete, userEmail }) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [channels, setChannels] = useState([]);
  const [expandedSeqId, setExpandedSeqId] = useState(null);

  // Auto-expand newest sequence when detail loads or sequences change
  useEffect(() => {
    if (detail && detail.sequences && detail.sequences.length > 0) {
      const newest = [...detail.sequences].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
      setExpandedSeqId(newest.id);
    }
  }, [detail]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('new');
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [deepEnriching, setDeepEnriching] = useState(false);
  const [showDeepConfirm, setShowDeepConfirm] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);
  const [verifiedIntel, setVerifiedIntel] = useState(null);
  const [generatingSeq, setGeneratingSeq] = useState(false);
  const [applyingIntel, setApplyingIntel] = useState(false);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/contacts/${contactId}/detail`);
        const data = await res.json();
        setDetail(data);
        try {
          const chRes = await fetch(`${API_BASE}/contacts/${contactId}/sequence-channels`);
          if (chRes.ok) setChannels(await chRes.json());
        } catch (e) { /* noop */ }
        setStatus(data.contact.status || 'new');
        // Load notes separately
        const notesRes = await fetch(`${API_BASE}/contacts/${contactId}/notes`);
        const notesData = await notesRes.json();
        setNotes(Array.isArray(notesData) ? notesData : []);
        // Check for platform verified intel
        const intelRes = await fetch(`${API_BASE}/contacts/${contactId}/verified-intel`).catch(() => null);
        if (intelRes) {
          const intelData = await intelRes.json();
          setVerifiedIntel(intelData.verified || null);
        }
      } catch (e) {}
      setLoading(false);
    }
    load();
  }, [contactId]);

  // Refetch contact detail when a sequence is generated externally
  useEffect(() => {
    function onGenerated(e) {
      if (e.detail?.contactId && String(e.detail.contactId) !== String(contactId)) return;
      fetch(`${API_BASE}/contacts/${contactId}/detail`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setDetail(d); })
        .catch(() => {});
    }
    window.addEventListener('ce:sequenceGenerated', onGenerated);
    return () => window.removeEventListener('ce:sequenceGenerated', onGenerated);
  }, [contactId]);

  async function handleStatusChange(newStatus) {
    setStatus(newStatus);
    await fetch(`${API_BASE}/contacts/${contactId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus })
    }).catch(() => {});
  }

  async function handlePostNote() {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      const res = await fetch(`${API_BASE}/contacts/${contactId}/notes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: clientId,
          author_email: userEmail,
          author_name: userEmail?.split('@')[0] || 'You',
          content: newNote.trim()
        })
      });
      const note = await res.json();
      setNotes(prev => [...prev, note]);
      setNewNote('');
    } catch (e) {}
    setSavingNote(false);
  }

  async function handleDeleteNote(noteId) {
    try {
      await fetch(`${API_BASE}/contacts/${contactId}/notes/${noteId}`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: userEmail })
      });
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (e) {}
  }

  async function handleApplyContactIntel() {
    if (!verifiedIntel) return;
    setApplyingIntel(true);
    try {
      await fetch(`${API_BASE}/contacts/${contactId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(verifiedIntel)
      });
      const res = await fetch(`${API_BASE}/contacts/${contactId}/detail`);
      setDetail(await res.json());
      setVerifiedIntel(null);
      toast.success('Intel applied to contact');
    } catch (e) {
      toast.error('Could not apply intel: ' + e.message);
    }
    setApplyingIntel(false);
  }

  async function handleEnrich(deep = false) {
    if (deep) { setDeepEnriching(true); } else { setEnriching(true); }
    const toastId = toast.loading(deep ? 'Deep enriching contact...' : 'Enriching contact...');
    try {
      const res = await creditAwareFetch(`${API_BASE}/contacts/${contactId}/enrich`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deep })
      });
      if (!res) return;
      const data = await res.json();
      if (data.success) {
        const res2 = await fetch(`${API_BASE}/contacts/${contactId}/detail`);
        setDetail(await res2.json());
        toast.success(deep ? 'Contact deep enriched' : 'Contact enriched', { id: toastId });
      } else {
        toast.error('Could not enrich contact', { id: toastId });
      }
    } catch (e) {
      toast.error('Enrichment failed: ' + e.message, { id: toastId });
    }
    if (deep) { setDeepEnriching(false); } else { setEnriching(false); }
  }

  function askDeepEnrich() {
    if (enriching || deepEnriching) return;
    setShowDeepConfirm(true);
  }

  async function runDeepEnrich() {
    setShowDeepConfirm(false);
    await handleEnrich(true);
  }

  async function handleScore() {
    setScoring(true);
    const toastId = toast.loading('Scoring prospect...');
    try {
      await fetch(`${API_BASE}/contacts/${contactId}/score`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      const res = await fetch(`${API_BASE}/contacts/${contactId}/detail`);
      setDetail(await res.json());
      toast.success('Prospect scored', { id: toastId });
    } catch (e) {
      toast.error('Scoring failed: ' + e.message, { id: toastId });
    }
    setScoring(false);
  }

  function startEdit(contact) {
    setEditForm({
      full_name: contact.full_name || '',
      job_title: contact.job_title || '',
      email: contact.email || '',
      phone: contact.phone || '',
      linkedin_url: contact.linkedin_url || '',
      cta_url: contact.cta_url || '',
      landing_page_url: contact.landing_page_url || '',
      attachment_url: contact.attachment_url || '',
      attachment_name: contact.attachment_name || '',
    });
    setEditError(null);
    setEditing(true);
  }

  async function handleSaveEdit() {
    setSavingEdit(true);
    setEditError(null);
    try {
      const res = await fetch(`${API_BASE}/contacts/${contactId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      if (!res.ok) throw new Error(await res.text());
      const res2 = await fetch(`${API_BASE}/contacts/${contactId}/detail`);
      setDetail(await res2.json());
      setEditing(false);
    } catch (e) {
      setEditError('Could not save. Please try again.');
    }
    setSavingEdit(false);
  }

  if (loading) return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '580px', height: '100vh', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#888' }}>Loading...</p>
      </div>
    </div>
  );

  if (!detail) return null;
  const { contact, signals, sequences, score } = detail;

  function getChannel(channel) {
    return channels.find(ch => ch.channel === channel) || null;
  }

  async function approveChannel(channelId) {
    try {
      const res = await fetch(`${API_BASE}/sequence-channels/${channelId}/approve`, { method: 'PUT' });
      if (!res.ok) throw new Error('Approve failed');
      const chRes = await fetch(`${API_BASE}/contacts/${contactId}/sequence-channels`);
      if (chRes.ok) setChannels(await chRes.json());
      const r = await fetch(`${API_BASE}/contacts/${contactId}/detail`);
      if (r.ok) setDetail(await r.json());
      toast.success('Channel approved');
    } catch (e) { toast.error(e.message); }
  }

  const enrichment = contact.enrichment_data || {};
  const scoreColour = score ? (score.total_score >= 8 ? '#276749' : score.total_score >= 6 ? '#B7791F' : '#888') : '#888';
  const scoreBg = score ? (score.total_score >= 8 ? '#F0FFF4' : score.total_score >= 6 ? '#FFFBEB' : '#F7F7F7') : '#F7F7F7';

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div onClick={e => e.stopPropagation()} style={{ width: '580px', height: '100vh', backgroundColor: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '0.5px solid #E8E4E0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '600', color: '#1A1A1A' }}>{contact.full_name}</h2>
                {score && <span style={{ fontSize: '0.75rem', fontWeight: '600', color: scoreColour, backgroundColor: scoreBg, padding: '0.2rem 0.5rem', borderRadius: '20px' }}>{score.total_score}/10</span>}
                <select value={status} onChange={e => handleStatusChange(e.target.value)}
                  style={{ fontSize: '0.72rem', fontWeight: '500', color: STATUS_COLOURS[status], backgroundColor: STATUS_BG[status], border: 'none', borderRadius: '20px', padding: '0.2rem 0.5rem', cursor: 'pointer', outline: 'none' }}>
                  {CONTACT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#888' }}>{contact.job_title} · <span style={{ color: '#8B1A1A', fontWeight: '500' }}>{contact.company_name}</span></p>
              <p style={{ margin: '0.15rem 0 0', fontSize: '0.75rem', color: '#AAA' }}>{contact.client_name}</p>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: '#888', cursor: 'pointer', flexShrink: 0 }}>×</button>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            <button onClick={async () => { setGeneratingSeq(true); await onSequence(contact.id, contact.client_id); setGeneratingSeq(false); }} disabled={generatingSeq}
              style={{ fontSize: '0.78rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.35rem 0.85rem', cursor: 'pointer' }}>
              {generatingSeq ? 'Generating...' : sequences.length > 0 ? 'Regenerate sequence' : 'Generate sequence →'}
            </button>
            <button onClick={() => handleEnrich(false)} disabled={enriching || deepEnriching}
              style={{ fontSize: '0.78rem', color: '#6B46C1', background: 'none', border: '1px solid #D4B8F5', borderRadius: '6px', padding: '0.35rem 0.75rem', cursor: 'pointer', opacity: (enriching || deepEnriching) ? 0.5 : 1 }}>
              {enriching ? 'Enriching...' : enrichment.insight ? 'Re-enrich' : 'Enrich'}
            </button>
            <button onClick={askDeepEnrich} disabled={enriching || deepEnriching}
              title="Extended web research — 6 credits"
              style={{ fontSize: '0.78rem', color: '#8B1A1A', background: 'none', border: '1px solid #E8B8B8', borderRadius: '6px', padding: '0.35rem 0.75rem', cursor: 'pointer', opacity: (enriching || deepEnriching) ? 0.5 : 1 }}>
              {deepEnriching ? 'Deep enriching...' : 'Deep enrich'}
            </button>
            <button onClick={handleScore} disabled={scoring}
              style={{ fontSize: '0.78rem', color: '#2B6CB0', background: 'none', border: '1px solid #BFD7F5', borderRadius: '6px', padding: '0.35rem 0.75rem', cursor: 'pointer' }}>
              {scoring ? 'Scoring...' : score ? 'Re-score' : 'Score'}
            </button>
            <button onClick={() => navigate('/clients/' + clientId + '/find-similar-contacts?seed=' + contact.id)}
              style={{ fontSize: '0.78rem', color: '#8B1A1A', background: '#fff', border: '1px solid #8B1A1A', borderRadius: '6px', padding: '0.35rem 0.85rem', cursor: 'pointer' }}>
              Find similar →
            </button>
            {contact.linkedin_url && (
              <a href={contact.linkedin_url.startsWith('http') ? contact.linkedin_url : 'https://' + contact.linkedin_url} target="_blank" rel="noreferrer"
                style={{ fontSize: '0.78rem', color: '#1A56A0', background: 'none', border: '1px solid #BFD7F5', borderRadius: '6px', padding: '0.35rem 0.75rem', textDecoration: 'none' }}>
                LinkedIn →
              </a>
            )}
            {onDelete && (
              <button onClick={() => { if (window.confirm('Delete this contact? This cannot be undone.')) onDelete(contactId); }}
                style={{ fontSize: '0.78rem', color: '#C53030', background: 'none', border: '1px solid #FEB2B2', borderRadius: '6px', padding: '0.35rem 0.75rem', cursor: 'pointer' }}>
                Delete
              </button>
            )}
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, marginTop: '0.75rem', borderBottom: '0.5px solid #E8E4E0' }}>
            {['overview', 'signals', 'sequences', 'notes'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                style={{ padding: '0.4rem 0.85rem', background: 'none', border: 'none', borderBottom: activeTab === tab ? '2px solid #8B1A1A' : '2px solid transparent', color: activeTab === tab ? '#8B1A1A' : '#888', fontSize: '0.8rem', cursor: 'pointer', fontWeight: activeTab === tab ? '500' : '400', textTransform: 'capitalize' }}>
                {tab}{tab === 'signals' && signals.length > 0 ? ` (${signals.length})` : ''}{tab === 'sequences' && sequences.length > 0 ? ` (${sequences.length})` : ''}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>

          {activeTab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

              {/* Platform intel banner */}
              {verifiedIntel && (
                <div style={{ backgroundColor: '#EBF8FF', border: '1px solid #BEE3F8', borderLeft: '4px solid #2B6CB0', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <div>
                      <p style={{ margin: '0 0 0.25rem', fontSize: '0.78rem', fontWeight: '600', color: '#2B6CB0' }}>Platform intelligence available</p>
                      <p style={{ margin: 0, fontSize: '0.72rem', color: '#4A5568' }}>Verified by {verifiedIntel.verified_count} update{verifiedIntel.verified_count !== 1 ? 's' : ''} across the platform</p>
                    </div>
                    <button onClick={() => setVerifiedIntel(null)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '1rem', cursor: 'pointer' }}>×</button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.75rem' }}>
                    {Object.entries(verifiedIntel).filter(([k]) => !['verified_count','last_verified_at'].includes(k)).map(([key, value]) => (
                      <div key={key} style={{ display: 'flex', gap: '0.5rem', fontSize: '0.78rem' }}>
                        <span style={{ color: '#888', minWidth: '80px', textTransform: 'capitalize' }}>{key.replace('_', ' ')}</span>
                        <span style={{ color: '#1A1A1A', fontWeight: '500' }}>{value}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={handleApplyContactIntel} disabled={applyingIntel}
                      style={{ fontSize: '0.78rem', color: '#fff', background: '#2B6CB0', border: 'none', borderRadius: '6px', padding: '0.35rem 0.85rem', cursor: 'pointer', opacity: applyingIntel ? 0.6 : 1 }}>
                      {applyingIntel ? 'Applying...' : 'Apply to this contact'}
                    </button>
                    <button onClick={() => setVerifiedIntel(null)}
                      style={{ fontSize: '0.78rem', color: '#888', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.35rem 0.75rem', cursor: 'pointer' }}>
                      Dismiss
                    </button>
                  </div>
                </div>
              )}

              {/* Contact info */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
                  <p style={{ margin: 0, fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: '500' }}>Contact information</p>
                  {!editing && (
                    <button onClick={() => startEdit(contact)}
                      style={{ fontSize: '0.72rem', color: '#4A5568', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>
                      Edit
                    </button>
                  )}
                </div>

                {editing ? (
                  <div style={{ backgroundColor: '#FAFAF8', border: '0.5px solid #8B1A1A', borderRadius: '8px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                    {[
                      { label: 'Full name', key: 'full_name', type: 'text' },
                      { label: 'Job title', key: 'job_title', type: 'text' },
                      { label: 'Email', key: 'email', type: 'email' },
                      { label: 'Phone', key: 'phone', type: 'text' },
                      { label: 'LinkedIn URL', key: 'linkedin_url', type: 'text' },
                      { label: 'CTA URL override (leave blank to use client default)', key: 'cta_url', type: 'url' },
                      { label: 'Landing page URL override (leave blank to use client default)', key: 'landing_page_url', type: 'url' },
                      { label: 'Attachment URL override (leave blank to use client default)', key: 'attachment_url', type: 'url' },
                      { label: 'Attachment name override (e.g. "2026 GTM Report")', key: 'attachment_name', type: 'text' },
                    ].map(field => (
                      <div key={field.key}>
                        <label style={{ display: 'block', fontSize: '0.72rem', color: '#888', marginBottom: '0.2rem', fontWeight: '500' }}>{field.label}</label>
                        <input
                          type={field.type}
                          value={editForm[field.key]}
                          onChange={e => setEditForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                          style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.4rem 0.6rem', fontSize: '0.82rem', color: '#1A1A1A', backgroundColor: '#fff', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }}
                        />
                      </div>
                    ))}
                    {editError && <p style={{ margin: 0, fontSize: '0.75rem', color: '#C53030' }}>{editError}</p>}
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                      <button onClick={handleSaveEdit} disabled={savingEdit}
                        style={{ fontSize: '0.78rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', opacity: savingEdit ? 0.6 : 1 }}>
                        {savingEdit ? 'Saving...' : 'Save changes'}
                      </button>
                      <button onClick={() => { setEditing(false); setEditError(null); }}
                        style={{ fontSize: '0.78rem', color: '#888', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.4rem 0.75rem', cursor: 'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ backgroundColor: '#FAFAF8', border: '0.5px solid #E8E4E0', borderRadius: '8px', padding: '0.85rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {contact.email && <p style={{ margin: 0, fontSize: '0.82rem', color: '#1A1A1A' }}><span style={{ color: '#888', marginRight: '0.5rem' }}>Email</span>{contact.email}</p>}
                    {contact.phone && <p style={{ margin: 0, fontSize: '0.82rem', color: '#1A1A1A' }}><span style={{ color: '#888', marginRight: '0.5rem' }}>Phone</span>{contact.phone}</p>}
                    {contact.company_name && <p style={{ margin: 0, fontSize: '0.82rem', color: '#1A1A1A' }}><span style={{ color: '#888', marginRight: '0.5rem' }}>Company</span>{contact.company_name}</p>}
                    {contact.sector && <p style={{ margin: 0, fontSize: '0.82rem', color: '#1A1A1A' }}><span style={{ color: '#888', marginRight: '0.5rem' }}>Sector</span>{contact.sector}</p>}
                    {contact.size && <p style={{ margin: 0, fontSize: '0.82rem', color: '#1A1A1A' }}><span style={{ color: '#888', marginRight: '0.5rem' }}>Size</span>{contact.size}</p>}
                    {contact.location && <p style={{ margin: 0, fontSize: '0.82rem', color: '#1A1A1A' }}><span style={{ color: '#888', marginRight: '0.5rem' }}>Location</span>{contact.location}</p>}
                    {contact.website && <p style={{ margin: 0, fontSize: '0.82rem' }}><span style={{ color: '#888', marginRight: '0.5rem' }}>Website</span><a href={contact.website.startsWith('http') ? contact.website : 'https://' + contact.website} target="_blank" rel="noreferrer" style={{ color: '#2B6CB0' }}>{contact.website}</a></p>}
                  </div>
                )}
              </div>

              {/* Score breakdown */}
              {score && (
                <div>
                  <p style={{ margin: '0 0 0.6rem', fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: '500' }}>Score breakdown</p>
                  <div style={{ backgroundColor: '#FAFAF8', border: '0.5px solid #E8E4E0', borderRadius: '8px', padding: '0.85rem 1rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      {[
                        { label: 'ICP Match', value: score.icp_score, weight: '40%' },
                        { label: 'Signals', value: score.signal_score, weight: '35%' },
                        { label: 'Enrichment', value: score.enrichment_score, weight: '25%' },
                      ].map(s => (
                        <div key={s.label} style={{ textAlign: 'center', padding: '0.5rem', backgroundColor: '#fff', borderRadius: '6px', border: '0.5px solid #E8E4E0' }}>
                          <p style={{ margin: '0 0 0.2rem', fontSize: '1.1rem', fontWeight: '600', color: '#1A1A1A' }}>{s.value}</p>
                          <p style={{ margin: 0, fontSize: '0.68rem', color: '#888' }}>{s.label}</p>
                          <p style={{ margin: 0, fontSize: '0.65rem', color: '#AAA' }}>{s.weight}</p>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.72rem', color: '#888' }}>Total score</span>
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: scoreColour }}>{score.total_score}/10</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Enrichment */}
              {enrichment.insight && (
                <div>
                  <p style={{ margin: '0 0 0.6rem', fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: '500' }}>Intelligence</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                    <div style={{ backgroundColor: '#F5F0FF', border: '0.5px solid #D4B8F5', borderRadius: '8px', padding: '0.85rem 1rem' }}>
                      <p style={{ margin: '0 0 0.35rem', fontSize: '0.68rem', fontWeight: '600', color: '#6B46C1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Insight</p>
                      <p style={{ margin: 0, fontSize: '0.82rem', color: '#1A1A1A', lineHeight: '1.6' }}>{enrichment.insight}</p>
                    </div>
                    {enrichment.buying_signals && (
                      <div style={{ backgroundColor: '#FFFBEB', border: '0.5px solid #F6E05E', borderRadius: '8px', padding: '0.85rem 1rem' }}>
                        <p style={{ margin: '0 0 0.35rem', fontSize: '0.68rem', fontWeight: '600', color: '#B7791F', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Buying signals</p>
                        <p style={{ margin: 0, fontSize: '0.82rem', color: '#1A1A1A', lineHeight: '1.6' }}>{enrichment.buying_signals}</p>
                      </div>
                    )}
                    {enrichment.recent_activity && (
                      <div style={{ backgroundColor: '#F7FAFC', border: '0.5px solid #E2E8F0', borderRadius: '8px', padding: '0.85rem 1rem' }}>
                        <p style={{ margin: '0 0 0.35rem', fontSize: '0.68rem', fontWeight: '600', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Recent activity</p>
                        <p style={{ margin: 0, fontSize: '0.82rem', color: '#1A1A1A', lineHeight: '1.6' }}>{enrichment.recent_activity}</p>
                      </div>
                    )}
                    {Array.isArray(enrichment.recent_posts) && enrichment.recent_posts.length > 0 && (
                      <div style={{ backgroundColor: '#F7FAFC', border: '0.5px solid #E2E8F0', borderRadius: '8px', padding: '0.85rem 1rem' }}>
                        <p style={{ margin: '0 0 0.5rem', fontSize: '0.68rem', fontWeight: '600', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Recent posts</p>
                        {enrichment.recent_posts.slice(0, 3).map((post, idx) => (
                          <div key={idx} style={{ marginBottom: idx < Math.min(2, enrichment.recent_posts.length - 1) ? '0.6rem' : 0, paddingBottom: idx < Math.min(2, enrichment.recent_posts.length - 1) ? '0.6rem' : 0, borderBottom: idx < Math.min(2, enrichment.recent_posts.length - 1) ? '0.5px solid #E2E8F0' : 'none' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
                              {post.date && <span style={{ fontSize: '0.7rem', color: '#888' }}>{new Date(post.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                              {post.source && <span style={{ fontSize: '0.65rem', color: '#888', background: '#EDF2F7', padding: '0.1rem 0.4rem', borderRadius: '20px', textTransform: 'capitalize' }}>{post.source}</span>}
                              {post.is_repost && <span style={{ fontSize: '0.65rem', color: '#6B46C1', background: '#FAF5FF', padding: '0.1rem 0.4rem', borderRadius: '20px' }}>Reposted</span>}
                              {post.url && <a href={post.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.7rem', color: '#1A56A0', textDecoration: 'none' }}>view ↗</a>}
                            </div>
                            <p style={{ margin: 0, fontSize: '0.8rem', color: '#1A1A1A', lineHeight: '1.5' }}>{(post.text || '').substring(0, 200)}{(post.text || '').length > 200 ? '...' : ''}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!enrichment.insight && (
                <div style={{ backgroundColor: '#FAFAF8', border: '0.5px solid #E8E4E0', borderRadius: '8px', padding: '1.5rem', textAlign: 'center' }}>
                  <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', color: '#888' }}>No enrichment data yet.</p>
                  <button onClick={() => handleEnrich(false)} disabled={enriching || deepEnriching} style={{ fontSize: '0.8rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', opacity: (enriching || deepEnriching) ? 0.5 : 1 }}>
                    {enriching ? 'Enriching...' : 'Enrich now'}
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'signals' && (
            <div>
              {signals.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                  <p style={{ margin: 0, fontSize: '0.85rem' }}>No signals detected for {contact.company_name} yet.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                  {signals.map(s => (
                    <div key={s.id} style={{ backgroundColor: SIGNAL_BG[s.signal_type] || '#F7F7F7', borderRadius: '8px', padding: '1rem', borderLeft: `3px solid ${SIGNAL_COLOURS[s.signal_type] || '#888'}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                        <span style={{ fontSize: '0.68rem', fontWeight: '600', color: SIGNAL_COLOURS[s.signal_type] || '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{SIGNAL_LABELS[s.signal_type] || s.signal_type}</span>
                        <span style={{ fontSize: '0.72rem', color: SIGNAL_COLOURS[s.signal_type] || '#888', fontWeight: '600' }}>Score {s.relevance_score}/10</span>
                      </div>
                      <p style={{ margin: '0 0 0.3rem', fontSize: '0.85rem', color: '#1A1A1A', lineHeight: '1.4', fontWeight: '500' }}>{s.signal_data?.summary}</p>
                      <p style={{ margin: 0, fontSize: '0.72rem', color: '#666' }}>
                        {s.source_url ? <a href={s.source_url} target="_blank" rel="noreferrer" style={{ color: '#2B6CB0' }}>{s.source}</a> : s.source}
                        {' · '}{new Date(s.detected_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'sequences' && (
            <div>
              <SequenceConfigPanel level="contact" id={contact.id} showResolutionChain={true} contactId={contact.id} />
              <div style={{ marginBottom: '0.75rem' }}>
                <button onClick={async () => { setGeneratingSeq(true); await onSequence(contact.id, contact.client_id); setGeneratingSeq(false); }}
                  disabled={generatingSeq}
                  style={{ fontSize: '0.8rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', fontWeight: '500', opacity: generatingSeq ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  {generatingSeq && <span style={{ display: 'inline-block', width: '10px', height: '10px', border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />}
                  {generatingSeq ? 'Generating...' : sequences.length > 0 ? 'Regenerate sequence' : 'Generate sequence →'}
                </button>
              </div>
              {sequences.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                  <p style={{ margin: 0, fontSize: '0.85rem' }}>No sequences generated yet.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {sequences.map(seq => {
                    const emailSteps = seq.email_sequence ? (typeof seq.email_sequence === 'string' ? JSON.parse(seq.email_sequence) : seq.email_sequence) || [] : [];
                    const linkedinSteps = seq.linkedin_sequence ? (typeof seq.linkedin_sequence === 'string' ? JSON.parse(seq.linkedin_sequence) : seq.linkedin_sequence) || [] : [];
                    return (
                      <div key={seq.id} style={{ border: '0.5px solid #E8E4E0', borderRadius: '8px', overflow: 'hidden' }}>
                        {/* Sequence header */}
                        <div onClick={() => setExpandedSeqId(expandedSeqId === seq.id ? null : seq.id)} style={{ padding: '0.75rem 1rem', backgroundColor: '#FAFAF8', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: expandedSeqId === seq.id ? '0.5px solid #E8E4E0' : 'none', cursor: 'pointer' }}>
                          <div>
                            <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '500', color: '#1A1A1A' }}>Version {seq.version}</p>
                            <p style={{ margin: '0.1rem 0 0', fontSize: '0.72rem', color: '#888' }}>Created {new Date(seq.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                            {(() => {
                              const emCh = getChannel('email');
                              const liCh = getChannel('linkedin');
                              return (
                                <>
                                  {emCh && (
                                    <span style={{ fontSize: '0.65rem', fontWeight: 600, color: emCh.approval_status === 'approved' ? '#276749' : '#B7791F', backgroundColor: emCh.approval_status === 'approved' ? '#F0FFF4' : '#FFFBEB', padding: '0.15rem 0.5rem', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                      Em · {emCh.approval_status}
                                    </span>
                                  )}
                                  {emCh && emCh.approval_status !== 'approved' && (
                                    <button onClick={(e) => { e.stopPropagation(); approveChannel(emCh.id); }}
                                      style={{ fontSize: '0.65rem', color: '#fff', background: '#276749', border: 'none', borderRadius: '4px', padding: '0.15rem 0.5rem', cursor: 'pointer', fontWeight: 500 }}>
                                      Approve email
                                    </button>
                                  )}
                                  {liCh && (
                                    <span style={{ fontSize: '0.65rem', fontWeight: 600, color: liCh.approval_status === 'approved' ? '#276749' : '#1A56A0', backgroundColor: liCh.approval_status === 'approved' ? '#F0FFF4' : '#EBF8FF', padding: '0.15rem 0.5rem', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                      Li · {liCh.approval_status}
                                    </span>
                                  )}
                                  {liCh && liCh.approval_status !== 'approved' && (
                                    <button onClick={(e) => { e.stopPropagation(); approveChannel(liCh.id); }}
                                      style={{ fontSize: '0.65rem', color: '#fff', background: '#1A56A0', border: 'none', borderRadius: '4px', padding: '0.15rem 0.5rem', cursor: 'pointer', fontWeight: 500 }}>
                                      Approve LinkedIn
                                    </button>
                                  )}
                                  {seq.pushed_at && <span style={{ fontSize: '0.65rem', color: '#1A56A0' }}>Pushed</span>}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                        {/* Email steps */}
                        {expandedSeqId === seq.id && emailSteps.length > 0 && (
                          <div style={{ padding: '0.75rem 1rem', borderBottom: linkedinSteps.length > 0 ? '0.5px solid #E8E4E0' : 'none' }}>
                            <p style={{ margin: '0 0 0.5rem', fontSize: '0.68rem', fontWeight: '700', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Email sequence</p>
                            {emailSteps.map((step, i) => (
                              <div key={i} style={{ marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: i < emailSteps.length - 1 ? '0.5px solid #F0EDE9' : 'none' }}>
                                <p style={{ margin: '0 0 0.15rem', fontSize: '0.68rem', color: '#AAA', textTransform: 'uppercase' }}>Step {step.step}</p>
                                {step.subject && <p style={{ margin: '0 0 0.15rem', fontSize: '0.82rem', fontWeight: '600', color: '#1A1A1A' }}>{step.subject}</p>}
                                <p style={{ margin: 0, fontSize: '0.78rem', color: '#4A5568', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{step.body}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* LinkedIn steps */}
                        {expandedSeqId === seq.id && linkedinSteps.length > 0 && (
                          <div style={{ padding: '0.75rem 1rem' }}>
                            <p style={{ margin: '0 0 0.5rem', fontSize: '0.68rem', fontWeight: '700', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.07em' }}>LinkedIn sequence</p>
                            {linkedinSteps.map((step, i) => (
                              <div key={i} style={{ marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: i < linkedinSteps.length - 1 ? '0.5px solid #F0EDE9' : 'none' }}>
                                <p style={{ margin: '0 0 0.15rem', fontSize: '0.68rem', color: '#AAA', textTransform: 'uppercase' }}>Step {step.step}{step.type ? ' — ' + step.type.replace(/_/g, ' ') : ''}</p>
                                <p style={{ margin: 0, fontSize: '0.78rem', color: '#4A5568', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{step.message}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'notes' && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {notes.length === 0 ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#AAA', fontSize: '0.85rem', fontStyle: 'italic', marginBottom: '1rem' }}>
                  No notes yet. Add the first one below.
                </div>
              ) : (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem', overflowY: 'auto' }}>
                  {notes.map(note => {
                    const isMe = note.author_email === userEmail;
                    const initials = (note.author_name || note.author_email || '?').substring(0, 2).toUpperCase();
                    const time = new Date(note.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
                    return (
                      <div key={note.id} style={{ display: 'flex', gap: '0.65rem', alignItems: 'flex-start', flexDirection: isMe ? 'row-reverse' : 'row' }}>
                        <div style={{ width: '30px', height: '30px', borderRadius: '50%', backgroundColor: isMe ? '#8B1A1A' : '#4A5568', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: '600', color: '#fff', flexShrink: 0 }}>
                          {initials}
                        </div>
                        <div style={{ maxWidth: '75%' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem', flexDirection: isMe ? 'row-reverse' : 'row' }}>
                            <span style={{ fontSize: '0.72rem', fontWeight: '500', color: '#4A5568' }}>{note.author_name || note.author_email}</span>
                            <span style={{ fontSize: '0.68rem', color: '#AAA' }}>{time}</span>
                          </div>
                          <div style={{ backgroundColor: isMe ? '#8B1A1A' : '#F5F5F5', color: isMe ? '#fff' : '#1A1A1A', borderRadius: isMe ? '12px 12px 2px 12px' : '12px 12px 12px 2px', padding: '0.6rem 0.85rem', fontSize: '0.83rem', lineHeight: '1.5', position: 'relative' }}>
                            {note.content}
                            {isMe && (
                              <button onClick={() => handleDeleteNote(note.id)}
                                style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#C53030', border: 'none', borderRadius: '50%', width: '16px', height: '16px', fontSize: '0.6rem', color: '#fff', cursor: 'pointer', display: 'none', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                                onMouseEnter={e => e.currentTarget.style.display = 'flex'}
                                className="note-delete-btn">
                                ×
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', borderTop: '0.5px solid #E8E4E0', paddingTop: '0.75rem' }}>
                <textarea
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePostNote(); } }}
                  placeholder="Add a note... (Enter to send, Shift+Enter for new line)"
                  rows={2}
                  style={{ flex: 1, border: '1px solid #D4CFC9', borderRadius: '8px', padding: '0.6rem 0.75rem', fontSize: '0.83rem', resize: 'none', fontFamily: 'inherit', lineHeight: '1.5', outline: 'none', boxSizing: 'border-box' }}
                />
                <button onClick={handlePostNote} disabled={savingNote || !newNote.trim()}
                  style={{ background: '#8B1A1A', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.6rem 1rem', fontSize: '0.83rem', cursor: 'pointer', flexShrink: 0, opacity: savingNote || !newNote.trim() ? 0.5 : 1 }}>
                  {savingNote ? '...' : 'Send'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showDeepConfirm && (
        <div onClick={() => !deepEnriching && setShowDeepConfirm(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '460px', width: '90%', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: 700, color: '#1A1A1A' }}>Deep enrich this contact?</h2>
            <p style={{ margin: '0 0 1.25rem', fontSize: '0.86rem', color: '#4A5568', lineHeight: 1.5 }}>
              Deep enrich uses extended web research to surface signals that standard enrichment may miss. Worth it for high-value contacts.
            </p>
            <div style={{ background: '#FAFAF8', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '1.5rem', fontSize: '0.83rem', color: '#4A5568' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                <span>Credits</span><strong>6 credits</strong>
              </div>
              <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '0.3rem' }}>
                Standard enrich is 3 credits and works well for most contacts.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button onClick={() => setShowDeepConfirm(false)} disabled={deepEnriching} style={{ flex: 1, padding: '0.65rem', fontSize: '0.85rem', background: '#fff', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={runDeepEnrich} disabled={deepEnriching} style={{ flex: 1, padding: '0.65rem', fontSize: '0.85rem', fontWeight: 600, background: '#8B1A1A', color: '#fff', border: 'none', borderRadius: '6px', cursor: deepEnriching ? 'not-allowed' : 'pointer' }}>
                {deepEnriching ? 'Starting...' : 'Deep enrich'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactPanel;
